/**
 * @fileoverview Update stage dialog for a My Page target.
 * @description Opened from the target's details by its assignee or a Super Admin when the backend
 * lists Update Stage among the allowed actions. Offers the stages the target may move to (forward,
 * or On Hold, Lost and Rejected with a required reason), then saves through Update Target Stage
 * with the version the details showed as `If-Match`, so a target that changed meanwhile is refused
 * and shown for review rather than overwritten. The parent mounts it only while it is open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { MY_PAGE_REASON_STAGES } from '@/features/sales/constants/my-page.constants'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { useUpdateMyPageTargetStage } from '@/features/sales/hooks/useUpdateMyPageTargetStage'
import { nextStageOptions } from '@/features/sales/lib/my-page-stage.utils'
import { isStaleMyPageError } from '@/features/sales/lib/my-page-stale.utils'
import { myPageStageFormSchema } from '@/features/sales/schemas/my-page-stage-form.schema'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import MyPageReasonField from './MyPageReasonField'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { MyPageStageFormValues } from '@/features/sales/schemas/my-page-stage-form.schema'
import type { MyPageStageCode, MyPageTargetDetails } from '@/features/sales/types/my-page.types'
import type { FormEvent } from 'react'

interface MyPageStageDialogProps {
  target: MyPageTargetDetails
  onClose: () => void
}

/**
 * @component
 * @param {MyPageStageDialogProps} props - The target and the close handler.
 * @param {MyPageTargetDetails} props.target - The target as the details last showed it.
 * @param {() => void} props.onClose - Called when the dialog is dismissed or the stage is saved.
 */
function MyPageStageDialog({ target, onClose }: MyPageStageDialogProps) {
  const { t } = useTranslation()
  const update = useUpdateMyPageTargetStage(onClose)
  const refreshSales = useRefreshSalesQueries()
  const close = useGuardedClose(onClose, update.isPending)
  const options = nextStageOptions(target.stage.code)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<MyPageStageFormValues>({
    resolver: zodResolver(myPageStageFormSchema),
    defaultValues: { toStage: '', reason: '' },
  })
  const toStage = useWatch({ control, name: 'toStage' })
  const needsReason = MY_PAGE_REASON_STAGES.includes(toStage as MyPageStageCode)
  const isStale = update.isError && isStaleMyPageError(update.error)

  /**
   * @description Saves the validated stage against the version the details showed.
   * @param {MyPageStageFormValues} values - The form's values.
   */
  const onValid = (values: MyPageStageFormValues) => {
    update.mutate({
      targetId: target.targetId,
      version: target.version,
      input: {
        toStage: values.toStage as MyPageStageCode,
        ...(needsReason ? { reason: values.reason } : {}),
      },
    })
  }

  /**
   * @description Reloads the latest state and returns to the details for review.
   */
  const onRefresh = () => {
    refreshSales()
    onClose()
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="my-page-stage-dialog"
      title={t('sales.myWork.dialogs.stage.title')}
      subtitle={target.bankName}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="my-page-stage-form"
      >
        <p className="text-[13.5px] text-ink" data-testid="my-page-stage-current">
          {t('sales.myWork.dialogs.stage.current', { stage: target.stage.label })}
        </p>
        <SelectField
          id="myPageToStage"
          data-testid="my-page-stage-select"
          label={t('sales.myWork.dialogs.stage.label')}
          options={[
            { value: '', label: t('sales.myWork.dialogs.stage.placeholder') },
            ...options.map((option) => ({ value: option.code, label: option.label })),
          ]}
          error={errors.toStage?.message ? t(errors.toStage.message) : undefined}
          {...register('toStage')}
        />
        {needsReason && (
          <MyPageReasonField
            id="myPageStageReason"
            data-testid="my-page-stage-reason"
            label={t('sales.myWork.dialogs.reasonRequiredLabel')}
            placeholder={t('sales.myWork.dialogs.reasonPlaceholder')}
            error={errors.reason?.message ? t(errors.reason.message) : undefined}
            {...register('reason')}
          />
        )}
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={false} />}
        {update.isError && !isStale && <SalesRequestError error={update.error} />}
        <DialogActions
          testIdPrefix="my-page-stage"
          cancelLabel={t('sales.myWork.dialogs.cancel')}
          confirmLabel={t('sales.myWork.dialogs.stage.submit')}
          onCancel={close}
          confirmType="submit"
          isLoading={update.isPending}
          disabled={isStale}
        />
      </form>
    </Modal>
  )
}

export default MyPageStageDialog
